<template>
  <div class="foreign-currency-main">
    <div class="foreign-currency-controls">
      <div class="foreign-currency-row">
        <div class="foreign-currency-column">
          <div class="foreign-currency-row">
            <Input
              textAlign="right"
              placeholder="Quantity"
              :style="{ maxWidth: '120px' }"
              :isRequired="true"
              :inputValue.sync="qty"
              :errorMessage.sync="qtyError"
              :isDisabled="viewMode || recordSource"
              validator="positiveDecimal,14,4"
            />
            <InputQuery
              placeholder="UOM"
              :items="uomList"
              itemKey="UOM Code"
              :selectedObject.sync="selectedUOM"
              :isDisabled="viewMode"
              :showDesc="false"
            />
          </div>
        </div>
        <div class="foreign-currency-column" :style="{ textAlign: 'center' }">
          <!-- {{ priceTitle }} -->
          <span v-if="buyingIsHidden"
            >Sell ({{ selectedSellingCurrency ? selectedSellingCurrency['Currency Code'] : '' }})</span
          >
          <span v-else-if="sellingIsHidden"
            >Buy ({{ selectedBuyingCurrency ? selectedBuyingCurrency['Currency Code'] : '' }})
          </span>
          <span v-else
            >Buy ({{ selectedBuyingCurrency ? selectedBuyingCurrency['Currency Code'] : '' }}) -&gt; Sell ({{
              selectedSellingCurrency ? selectedSellingCurrency['Currency Code'] : ''
            }})</span
          >
        </div>
        <div class="foreign-currency-column" :style="{ textAlign: 'center' }"> MYR Equivalent </div>
      </div>
      <BoxContainer
        v-if="!buyingIsHidden"
        title="Buying"
        background="rgba(225, 255, 255)"
        class="foreign-currency-box-container"
        style="margin-bottom: 15px"
      >
        <div class="foreign-currency-row">
          <div class="foreign-currency-column">
            <InputQuery
              placeholder="Currency"
              :isRequired="true"
              :items="currencyList"
              :isDisabled="fieldDisabled || viewMode || recordSource"
              :allowFreeText="false"
              itemKey="Currency Code"
              descKey="Description"
              :selectedObject.sync="selectedBuyingCurrency"
              @selected="currencySelected($event, true)"
              :errorMessage.sync="buyingCurrencyError"
              :showDesc="false"
              :tableBoxDataType="tableDataType"
            />
            <div>
              <slot name="buying-first-col"></slot>
            </div>
          </div>
          <div class="foreign-currency-column" style="width: 33.33%">
            <!-- couldn't find another way, flex: 1 doesn't work -->
            <div class="foreign-currency-row">
              <Input
                placeholder="U. Price"
                :isRequired="true"
                :isDisabled="fieldDisabled || viewMode || recordSource"
                textAlign="right"
                :inputValue.sync="buyingUnitPrice"
                @focusChanged="buyingPricesFocusOut($event, 'buyingUnitPrice')"
                :validator="`${allowNegative},14,4`"
                :errorMessage.sync="buyingUnitPriceError"
              />
              <Input
                placeholder="Total"
                :isRequired="true"
                :isDisabled="fieldDisabled || viewMode || recordSource"
                textAlign="right"
                :inputValue.sync="buyingTotalPrice"
                @focusChanged="buyingPricesFocusOut($event, 'buyingTotalPrice')"
                :validator="`${allowNegative},14,2`"
                :errorMessage.sync="buyingTotalPriceError"
              />
              <Input
                placeholder="Exch. Rate"
                :isRequired="true"
                :isDisabled="fieldDisabled || viewMode || disabledBuyRate || recordSource"
                textAlign="right"
                :inputValue.sync="buyingExchRate"
                validator="positiveDecimal,,4"
                :errorMessage.sync="buyingExchRateError"
              />
            </div>
            <div>
              <slot name="buying-second-col"></slot>
            </div>
          </div>
          <div class="foreign-currency-column">
            <div class="foreign-currency-row">
              <Input placeholder="U. Price" :isDisabled="true" :inputValue="myrEquivalentBuyingUnitPrice" />
              <Input placeholder="Total" :isDisabled="true" :inputValue="myrEquivalentBuyingTotalPrice" />
            </div>
            <div>
              <slot name="buying-third-col"></slot>
            </div>
          </div>
        </div>
      </BoxContainer>
      <BoxContainer
        v-if="!markupIsHidden"
        title="Mark Up"
        background="rgba(225, 255, 255)"
        class="foreign-currency-box-container"
        style="margin-bottom: 15px"
      >
        <div class="foreign-currency-row">
          <div class="foreign-currency-column">
            <div>
              <slot name="markup-first-col"></slot>
            </div>
          </div>
          <div class="foreign-currency-column">
            <div class="foreign-currency-row">
              <Input
                placeholder="U. Price"
                :isRequired="true"
                :isDisabled="fieldDisabled || viewMode || (!sameBuyingSellingCurrency && allowDiffCurr != 'N')"
                textAlign="right"
                :inputValue.sync="markupUnitPrice"
                @focusChanged="markupPriceFocusOut($event, 'markupUnitPrice')"
                :validator="`${allowNegative},14,4`"
                :errorMessage.sync="markupUnitPriceError"
              />
              <Input
                placeholder="Total"
                :isRequired="true"
                :isDisabled="fieldDisabled || viewMode || (!sameBuyingSellingCurrency && allowDiffCurr != 'N')"
                textAlign="right"
                :inputValue.sync="markupTotalPrice"
                @focusChanged="markupPriceFocusOut($event, 'markupTotalPrice')"
                :validator="`${allowNegative},14,2`"
                :errorMessage.sync="markupTotalPriceError"
              />
              <div class="foreign-currency-column"></div>
            </div>
          </div>
          <div class="foreign-currency-column">
            <div class="foreign-currency-row">
              <Input placeholder="U. Price" :isDisabled="true" :inputValue="myrEquivalentMarkupUnitPrice" />
              <Input placeholder="Total" :isDisabled="true" :inputValue="myrEquivalentMarkupTotalPrice" />
            </div>
            <div>
              <slot name="markup-third-col"></slot>
            </div>
          </div>
        </div>
      </BoxContainer>
      <BoxContainer
        v-if="!sellingIsHidden"
        title="Selling"
        background="rgba(225, 255, 255)"
        class="foreign-currency-box-container"
      >
        <div class="foreign-currency-row">
          <div class="foreign-currency-column">
            <div class="foreign-currency-row">
              <InputQuery
                placeholder="Currency"
                :isRequired="true"
                :items="currencyList"
                :isDisabled="fieldDisabled || viewMode"
                :allowFreeText="false"
                itemKey="Currency Code"
                descKey="Description"
                :selectedObject.sync="selectedSellingCurrency"
                @selected="currencySelected($event, false)"
                :errorMessage.sync="sellingCurrencyError"
                :showDesc="false"
                :tableBoxDataType="tableDataType"
              />
            </div>
            <div>
              <div v-if="enableTaxSection">
                <div v-if="GSTEnabled" style="display: flex; flex-direction: row">
                  <InputQuery
                    placeholder="Tax Code"
                    itemKey="Tax Code"
                    :allowFreeText="false"
                    descKey="Tax Code Desc"
                    :showDesc="false"
                    :isDisabled="fieldDisabled || viewMode || disabledTaxCodeField"
                    :items="taxCodeList"
                    :selectedObject.sync="selectedTaxCode"
                  />
                  <Input
                    placeholder="Tax Rate %"
                    style="width: 50%; margin-left: 4px"
                    :isDisabled="true"
                    textType="number"
                    textAlign="right"
                    :inputValue.sync="taxRate"
                  />
                </div>
                <div style="display: flex; flex-direction: row">
                  <InputQuery
                    placeholder="Tax Code"
                    itemKey="Tax Code"
                    :allowFreeText="false"
                    descKey="Tax Code Desc"
                    :showDesc="false"
                    :isDisabled="fieldDisabled || viewMode || disabledTaxCodeField"
                    :items="sstTaxCodeList"
                    :selectedObject.sync="selectedsstTaxCode"
                    @focusChanged="taxPriceFocusOut($event, 'selectedsstTaxCode')"
                  />
                  <Input
                    style="width: 50%; margin-left: 4px"
                    placeholder="Srv. Tax Rate %"
                    :isDisabled="true"
                    textType="number"
                    textAlign="right"
                    :inputValue.sync="srvTaxRate"
                  />
                </div>
              </div>
              <slot name="selling-first-col"></slot>
            </div>
          </div>
          <div class="foreign-currency-column" style="width: 33.33%">
            <!-- couldn't find another way, flex: 1 doesn't work -->
            <div class="foreign-currency-row">
              <Input
                placeholder="U. Price"
                :isRequired="true"
                :isDisabled="fieldDisabled || viewMode"
                textAlign="right"
                :inputValue.sync="sellingUnitPrice"
                @focusChanged="sellingPriceFocusOut($event, 'sellingUnitPrice')"
                :validator="`${allowNegative},14,4`"
                :errorMessage.sync="sellingUnitPriceError"
              />
              <Input
                placeholder="Total"
                :isRequired="true"
                :isDisabled="fieldDisabled || viewMode"
                textAlign="right"
                :inputValue.sync="sellingTotalPrice"
                @focusChanged="sellingPriceFocusOut($event, 'sellingTotalPrice')"
                :validator="`${allowNegative},14,2`"
                :errorMessage.sync="sellingTotalPriceError"
              />
              <Input
                placeholder="Exch. Rate"
                :isRequired="true"
                :isDisabled="fieldDisabled || viewMode || disabledSellRate"
                textAlign="right"
                :inputValue.sync="sellingExchRate"
                validator="positiveDecimal,,4"
                :errorMessage.sync="sellingExchRateError"
              />
            </div>
            <div>
              <div v-if="GSTEnabled & enableTaxSection" style="display: flex; flex-direction: row">
                <div style="width: 50%; margin-right: 4px"></div>
                <Input
                  placeholder="Tax Amt"
                  style="width: 50%; margin-left: 4px"
                  :isDisabled="true"
                  textType="number"
                  textAlign="right"
                  :inputValue.sync="ftaxsAmt"
                />
              </div>
              <div v-if="enableTaxSection" style="display: flex; flex-direction: row">
                <Input
                  style="width: 50%; margin-right: 3px"
                  placeholder="Srv. Tax Amt"
                  :isDisabled="true"
                  textType="number"
                  textAlign="right"
                  :inputValue.sync="fsrvTaxAmt"
                />
                <Input
                  style="width: 50%; margin-left: 3px"
                  placeholder="Net Incl Tax"
                  :isDisabled="true"
                  textType="number"
                  textAlign="right"
                  :inputValue.sync="fnetInclTax"
                />
              </div>
              <slot name="selling-second-col"></slot>
            </div>
          </div>
          <div class="foreign-currency-column">
            <div class="foreign-currency-row">
              <Input placeholder="U. Price" :isDisabled="true" :inputValue="myrEquivalentSellingUnitPrice" />
              <Input placeholder="Total" :isDisabled="true" :inputValue="myrEquivalentSellingTotalPrice" />
            </div>
            <div>
              <div v-if="GSTEnabled & enableTaxSection" style="display: flex; flex-direction: row">
                <div style="width: 50%; margin-right: 3px"></div>
                <Input
                  placeholder="Tax Amt"
                  style="width: 50%; margin-left: 3px"
                  :isDisabled="fieldDisabled || viewMode"
                  textType="number"
                  textAlign="right"
                  :inputValue.sync="taxAmt"
                />
              </div>
              <div style="display: flex; flex-direction: row" v-if="enableTaxSection">
                <Input
                  placeholder="Srv. Tax Amt"
                  style="margin-right: 3px"
                  :isDisabled="true"
                  textType="number"
                  textAlign="right"
                  :inputValue.sync="srvTaxAmt"
                />
                <Input
                  placeholder="Net Incl. Tax"
                  :errorMessage.sync="netInclTaxError"
                  :isRequired="true"
                  style="margin-left: 3px"
                  :isDisabled="fieldDisabled || viewMode"
                  textType="number"
                  textAlign="right"
                  :inputValue.sync="netInclTax"
                  @focusChanged="taxPriceFocusOut($event, 'myrEquivalentSellingTotalPrice')"
                />
              </div>
              <slot name="selling-third-col"></slot>
            </div>
          </div>
        </div>
      </BoxContainer>
    </div>

    <div class="foreign-currency-loading" v-if="isLoading">
      <div class="foreign-currency-loading-spinner"></div>
    </div>

    <div class="foreign-currency-background">
      <div class="background-left"></div>
      <div class="background-middle"></div>
      <div class="background-right"></div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  components: {},
  data: function () {
    return {
      isLoading: false,
      allowDiffCurr: '',
      uomList: [],
      currencyList: [],
      disabledTaxCodeField: false,
      qty: '',
      selectedUOM: null,

      selectedBuyingCurrency: null,
      selectedBuyingCurrencyOld: null,
      buyingUnitPrice: '',
      buyingTotalPrice: '',
      buyingExchRate: '',
      buyingFactor: '',

      markupUnitPrice: '',
      markupTotalPrice: '',

      selectedSellingCurrency: null,
      selectedSellingCurrencyOld: null,
      sellingUnitPrice: '',
      sellingUnitPriceDisabled: '0',
      sellingTotalPrice: '',
      sellingTotalPriceDisabled: '0',
      sellingExchRate: '',
      sellingExchRateDisabled: '0',
      sellingFactor: '',
      tableDataType: null,
      warnBuyingCurrencyChanged: false,
      warnSellingCurrencyChanged: false,

      //#region Error Messages
      qtyError: '',
      buyingCurrencyError: '',
      buyingUnitPriceError: '',
      buyingTotalPriceError: '',
      buyingExchRateError: '',
      buyingExchRateDisabled: '0',
      buyingTotalPriceDisabled: '0',
      buyingUnitPriceDisabeld: '0',

      markupUnitPriceError: '',
      markupTotalPriceError: '',

      sellingCurrencyError: '',
      sellingUnitPriceError: '',
      sellingTotalPriceError: '',
      sellingExchRateError: '',
      netInclTaxError: '',

      // payableChecking: '',
      //#endregion

      //#region Tax
      GSTEnabled: false,
      taxCodeList: [],
      selectedTaxCode: null,
      taxRate: '',
      taxAmt: '',
      ftaxAmt: '',

      sstTaxCodeList: [],
      selectedsstTaxCode: null,
      srvTaxRate: '0',
      fsrvTaxRate: '',
      netInclTax: '', //MYR equivalent
      fnetInclTax: '',
      srvTaxAmt: '',
      fsrvTaxAmt: '',
      //#endregion
    };
  },
  props: {
    details: {
      type: Object,
      default: function () {
        return null;
      },
    },
    payableChecking: { type: String, default: '1' },
    selectedChargesCode: {
      type: Object,
      default: function () {
        return null;
      },
    },
    taxCodeRate: {
      type: Object,
      default: function () {
        return null;
      },
    },
    action: { type: String, default: 'N' },
    viewMode: { type: Boolean, default: false },
    recordSource: { type: Boolean, default: false },
    enableTaxSection: { type: Boolean, default: false },
    internalCustomer: { type: String, default: '0' },
    headerCurrency: {
      type: Object,
      default: function () {
        return null;
      },
    },
    headerRate: { type: String, default: '' },
    allowNegativeAmt: { type: Boolean, default: false },
    // public events
    // @onCurrencyChanged - fired when the currency is selected
  },
  computed: {
    disabledSellRate: function () {
      if (!this.selectedSellingCurrency) {
        // to disable the field if cannot change
        return false;
      } else {
        return (
          this.selectedSellingCurrency['Currency Code'] == 'MYR' &&
          this.sellingExchRate == Number(this.selectedSellingCurrency['Currency Rate']).toFixed(4).toString()
        );
      }
    },
    disabledBuyRate: function () {
      if (!this.selectedBuyingCurrency) {
        return false;
      } else {
        return (
          this.selectedBuyingCurrency['Currency Code'] == 'MYR' &&
          this.buyingExchRate == Number(this.selectedBuyingCurrency['Currency Rate']).toFixed(4).toString()
        );
      }
    },
    buyingIsHidden: function () {
      // to add the condition of the SAS based on role here
      return (this.action == 'N' || this.action == 'E') && this.payableChecking == '0';
    },
    sellingIsHidden: function () {
      // to add the condition of the SAS based on role here
      return (this.action == 'N' || this.action == 'E') && this.payableChecking == '2';
    },
    markupIsHidden: function () {
      // to add the condition of the SAS based on role here
      return (
        this.buyingIsHidden ||
        this.sellingIsHidden ||
        (!this.sameBuyingSellingCurrency &&
          this.allowDiffCurr != 'N' &&
          (this.selectedBuyingCurrency || this.selectedSellingCurrency)) ||
        this.buyingExchRate != this.sellingExchRate
      );
    },
    fieldDisabled: function () {
      return !this.qty;
    },
    sameBuyingSellingCurrency: function () {
      if (!this.selectedBuyingCurrency || !this.selectedSellingCurrency) {
        return false;
      }
      return this.selectedBuyingCurrency['Currency Code'] == this.selectedSellingCurrency['Currency Code'];
      // includes('->'), then true
    },
    myrEquivalentBuyingUnitPrice: function () {
      if (this.buyingUnitPrice && this.buyingExchRate) {
        return ((Number(this.buyingUnitPrice) * Number(this.buyingExchRate)) / Number(this.buyingFactor)).toFixed(4);
      } else {
        return '';
      }
    },
    myrEquivalentBuyingTotalPrice: function () {
      if (this.buyingTotalPrice && this.buyingExchRate) {
        return ((Number(this.buyingTotalPrice) * Number(this.buyingExchRate)) / Number(this.buyingFactor)).toFixed(2);
      } else {
        return '';
      }
    },
    myrEquivalentSellingUnitPrice: function () {
      if (this.sellingUnitPrice && this.sellingExchRate) {
        return ((Number(this.sellingUnitPrice) * Number(this.sellingExchRate)) / Number(this.sellingFactor)).toFixed(4);
      } else {
        return '';
      }
    },
    myrEquivalentSellingTotalPrice: function () {
      if (this.sellingTotalPrice && this.sellingExchRate) {
        return ((Number(this.sellingTotalPrice) * Number(this.sellingExchRate)) / Number(this.sellingFactor)).toFixed(
          2,
        );
      } else {
        return '';
      }
    },
    myrEquivalentMarkupUnitPrice: function () {
      if (this.sameBuyingSellingCurrency && this.payableChecking != '2' && this.markupUnitPrice != '0.0000') {
        return (Number(this.myrEquivalentSellingUnitPrice) - Number(this.myrEquivalentBuyingUnitPrice)).toFixed(4);
      } else if (this.markupUnitPrice) {
        return (Number(this.markupUnitPrice) * Number(this.buyingExchRate)).toFixed(4);
      } else {
        return '';
      }
    },
    myrEquivalentMarkupTotalPrice: function () {
      if (this.sameBuyingSellingCurrency && this.payableChecking != '2' && this.markupTotalPrice != '0.00') {
        return (Number(this.myrEquivalentSellingTotalPrice) - Number(this.myrEquivalentBuyingTotalPrice)).toFixed(2);
      } else if (this.markupTotalPrice) {
        return (Number(this.markupTotalPrice) * Number(this.buyingExchRate)).toFixed(2);
      } else {
        return '';
      }
    },
    allowNegative: function () {
      return this.allowNegativeAmt ? 'decimal' : 'positiveDecimal';
    },
  },
  methods: {
    getExchangeRate: async function (code) {
      if (!code) {
        return '';
      }

      this.isLoading = true;
      let exchRate =
        code ? await this.$apiCallV2('/Master/GetDetailsMaster', { masterfile: 'exchangerate', recordid: code }) : [];
      this.isLoading = false;

      if (exchRate.length > 0) {
        return [exchRate[0]['Var1'], exchRate[0]['Ex_Factor']];
      } else {
        return ['', ''];
      }
    },
    calculateTax: function () {
      if (this.enableTaxSection) {
        this.srvTaxRate = this.srvTaxRate ? this.srvTaxRate : '0';
        let myrTot = (
          (Number(this.sellingTotalPrice) * Number(this.sellingExchRate)) /
          Number(this.sellingFactor)
        ).toFixed(2); //myr equivalent selling total price
        let temp = this.srvTaxRate ? (Number(this.srvTaxRate) / 100) * Number(myrTot) : '0.00';
        this.srvTaxAmt = temp.toFixed(2).toString();

        let tempfsrvTaxAmt =
          this.srvTaxRate ? (Number(this.srvTaxRate) / 100) * Number(this.sellingTotalPrice) : '0.00';
        this.fsrvTaxAmt = tempfsrvTaxAmt.toFixed(2).toString();

        this.netInclTax = (Number(myrTot) + Number(this.srvTaxAmt)).toFixed(2);
        this.fnetInclTax = (Number(this.sellingTotalPrice) + Number(this.fsrvTaxAmt)).toFixed(2);
      }
    },
    buyingPricesFocusOut: function (state, currentData) {
      if (!state) {
        this.clearErrMsg();
        // if (!this.markupUnitPrice) {
        //   this.markupUnitPrice = Number('0').toFixed(4);
        // }
        // if (!this.markupTotalPrice) {
        //   this.markupTotalPrice = Number('0').toFixed(2);
        // }
        if (currentData == 'buyingUnitPrice') {
          this.buyingTotalPrice = (Number(this.buyingUnitPrice) * Number(this.qty)).toFixed(2);
          this.buyingUnitPrice = Number(this.buyingUnitPrice).toFixed(4);
        } else {
          this.buyingUnitPrice = (Number(this.buyingTotalPrice) / Number(this.qty)).toFixed(4);
          this.buyingTotalPrice = Number(this.buyingTotalPrice).toFixed(2);
        }
        if (!this.sellingIsHidden) {
          this.sellingUnitPrice = (Number(this.buyingUnitPrice) + Number(this.markupUnitPrice)).toFixed(4);
          this.sellingTotalPrice = (Number(this.buyingTotalPrice) + Number(this.markupTotalPrice)).toFixed(2);
        } else {
          this.sellingUnitPrice = '0.00';
          this.sellingTotalPrice = '0.00';
        }
        this.calculateTax();
      }
    },
    markupPriceFocusOut: function (state, currentData) {
      if (!state) {
        this.clearErrMsg();
        if (currentData == 'markupUnitPrice') {
          this.markupTotalPrice = (Number(this.markupUnitPrice) * Number(this.qty)).toFixed(2);
          this.markupUnitPrice = Number(this.markupUnitPrice).toFixed(4);
        } else {
          this.markupUnitPrice = (Number(this.markupTotalPrice) / Number(this.qty)).toFixed(4);
          this.markupTotalPrice = Number(this.markupTotalPrice).toFixed(2);
        }

        this.sellingUnitPrice = (Number(this.buyingUnitPrice) + Number(this.markupUnitPrice)).toFixed(4);
        this.sellingTotalPrice = (Number(this.buyingTotalPrice) + Number(this.markupTotalPrice)).toFixed(2);
        this.calculateTax();
      }
    },
    taxPriceFocusOut: function (state, currentData) {
      if (!state) {
        //  if (!this.markupUnitPrice) {
        //   this.markupUnitPrice = Number('0').toFixed(4);
        // }
        // if (!this.markupTotalPrice) {
        //   this.markupTotalPrice = Number('0').toFixed(2);
        // }
        if (currentData == 'myrEquivalentSellingTotalPrice') {
          let temp = Number(this.netInclTax) - Number(this.srvTaxAmt);
          this.sellingTotalPrice = ((Number(temp) / Number(this.sellingExchRate)) * Number(this.sellingFactor)).toFixed(
            2,
          );
          this.sellingUnitPrice = (Number(this.sellingTotalPrice) / Number(this.qty)).toFixed(4);
        }
        if (!this.buyingIsHidden) {
          this.markupUnitPrice = (Number(this.sellingUnitPrice) - Number(this.buyingUnitPrice)).toFixed(4);
          this.markupTotalPrice = (Number(this.sellingTotalPrice) - Number(this.buyingTotalPrice)).toFixed(2);
        } else {
          this.buyingUnitPrice = '0.00';
          this.buyingTotalPrice = '0.00';
          this.markupUnitPrice = Number(this.sellingUnitPrice).toFixed(4);
          this.markupTotalPrice = Number(this.sellingTotalPrice).toFixed(2);
        }

        if (currentData == 'selectedsstTaxCode') {
          if (this.srvTaxRate == '0' && this.internalCustomer != '0') {
            this.$showAlert('error', 'Service Tax does not apply to charge code', 'Exemption not applicable');
            this.selectedsstTaxCode = null;
            this.sstTaxCode = null;
            return;
          } else if (!this.selectedsstTaxCode) {
            this.srvTaxRate = this.selectedChargesCode['SST Rate %'].toString();
          }
          this.srvTaxRate =
            this.selectedsstTaxCode ?
              this.selectedsstTaxCode['RATE'].toString()
            : (this.selectedChargesCode['SST Rate %'].toString() ?? '');
          this.sstTaxCode = this.selectedsstTaxCode?.['Tax Code'] ?? '';
        }
        this.calculateTax();
      }
    },
    sellingPriceFocusOut: function (state, currentData) {
      if (!state) {
        this.clearErrMsg();
        // if (!this.markupUnitPrice && !this.enableTaxSection) {
        //   this.markupUnitPrice = Number('0').toFixed(4);
        // }else{
        //   this.markupUnitPrice = (Number(this.sellingUnitPrice)).toFixed(4);
        // }
        // if (!this.markupTotalPrice && !this.enableTaxSection) {
        //   this.markupTotalPrice = Number('0').toFixed(2);
        // }else{
        //   this.markupTotalPrice = (Number(this.sellingTotalPrice)).toFixed(4);
        // }
        if (currentData == 'sellingUnitPrice') {
          this.sellingTotalPrice = (Number(this.sellingUnitPrice) * Number(this.qty)).toFixed(2);
          this.sellingUnitPrice = Number(this.sellingUnitPrice).toFixed(4);
        } else {
          this.sellingUnitPrice = (Number(this.sellingTotalPrice) / Number(this.qty)).toFixed(4);
          this.sellingTotalPrice = Number(this.sellingTotalPrice).toFixed(2);
        }
        if (!this.buyingIsHidden) {
          this.markupUnitPrice = (Number(this.sellingUnitPrice) - Number(this.buyingUnitPrice)).toFixed(4);
          this.markupTotalPrice = (Number(this.sellingTotalPrice) - Number(this.buyingTotalPrice)).toFixed(2);
        } else {
          this.buyingUnitPrice = '0.00';
          this.buyingTotalPrice = '0.00';
          this.markupUnitPrice = Number(this.sellingUnitPrice).toFixed(4);
          this.markupTotalPrice = Number(this.sellingTotalPrice).toFixed(2);
        }
        this.calculateTax();
      }
    },
    currencySelected: async function (curr, isBuying) {
      this.$emit('onCurrencyChanged', curr, isBuying);
      if (isBuying) {
        if (this.selectedBuyingCurrencyOld) {
          if (
            this.selectedBuyingCurrencyOld['Currency Code'] == curr['Currency Code'] &&
            this.buyingExchRate != Number(curr['Currency Rate']).toFixed(4).toString() &&
            this.buyingExchRate != ''
          ) {
            let changeRate = await this.$showAlert(
              'warning',
              `Exchange Rate Mismatch`,
              `The exchange rate for ${curr['Currency Code']} is ${Number(curr['Currency Rate']).toFixed(4).toString()} but it has been changed to ${this.buyingExchRate}.<br>Do you want to change to the last exchange rate or remain the current exchange rate?`,
              [
                { text: 'Change to Latest Exchange Rate' },
                { text: 'Remain Current Exchange Rate', color: 'indianred', textColor: 'white' },
              ],
            );

            if (changeRate.includes('Latest')) {
              this.buyingExchRate = curr['Currency Rate']?.toString();
              this.sellingExchRate = curr['Currency Rate']?.toString();
              this.selectedSellingCurrency = this.selectedBuyingCurrency;
            }
          } else {
            this.selectedBuyingCurrency = curr;
            if (
              this.allowDiffCurr == 'Y' &&
              this.selectedBuyingCurrency['Currency Code'] != this.selectedSellingCurrency['Currency Code']
            ) {
              let changeCurrency = await this.$showAlert(
                'warning',
                'Different Currency Detected',
                `Do you want to change selling currency to ${this.selectedBuyingCurrency['Currency Code']}`,
                [
                  { text: `Change Selling Currency to ${this.selectedBuyingCurrency['Currency Code']}` },
                  { text: 'Remain Current Selling Currency ', color: 'indianred', textColor: 'white' },
                ],
              );

              if (changeCurrency.startsWith('Change')) {
                this.sellingExchRate = curr['Currency Rate']?.toString();
                this.selectedSellingCurrency = this.selectedBuyingCurrency;
              }
            } else if (this.allowDiffCurr == 'N' && this.recordSource == false) {
              this.sellingExchRate = curr['Currency Rate']?.toString();
              this.selectedSellingCurrency = this.selectedBuyingCurrency;
            } else if (
              this.allowDiffCurr == 'N' &&
              this.selectedBuyingCurrency['Currency Code'] != this.selectedBuyingCurrencyOld['Currency Code']
            ) {
              this.$showAlert(
                'error',
                'Different Currency Detected',
                'You are not allowed to have different currency. Please contact administartor',
              );
              this.$nextTick(() => {
                this.selectedBuyingCurrency = this.selectedBuyingCurrencyOld;
              });
              return;
            }
            this.buyingExchRate = curr['Currency Rate']?.toString();
          }
        } else {
          this.buyingExchRate = curr['Currency Rate']?.toString();
          if (
            this.allowDiffCurr == 'Y' &&
            this.selectedBuyingCurrency['Currency Code'] != this.selectedSellingCurrency['Currency Code']
          ) {
            let changeCurrency = await this.$showAlert(
              'warning',
              'Different Currency Detected',
              `Do you want to change selling currency to ${this.selectedBuyingCurrency['Currency Code']}`,
              [
                { text: `Change Selling Currency to ${this.selectedBuyingCurrency['Currency Code']}` },
                { text: 'Remain Current Selling Currency ', color: 'indianred', textColor: 'white' },
              ],
            );

            if (changeCurrency.startsWith('Change')) {
              this.sellingExchRate = curr['Currency Rate']?.toString();
              this.selectedSellingCurrency = this.selectedBuyingCurrency;
            }
          } else if (this.allowDiffCurr == 'N') {
            this.sellingExchRate = curr['Currency Rate']?.toString();
            this.selectedSellingCurrency = this.selectedBuyingCurrency;
          }
        }

        this.selectedSellingCurrencyOld = JSON.parse(JSON.stringify(this.selectedSellingCurrency));
        this.sellingFactor = this.selectedSellingCurrency['Factor'];

        this.selectedBuyingCurrencyOld = JSON.parse(JSON.stringify(curr));
        this.buyingFactor = curr['Factor'];
      } else {
        if (this.selectedSellingCurrencyOld) {
          if (
            this.selectedSellingCurrencyOld['Currency Code'] == curr['Currency Code'] &&
            this.sellingExchRate != Number(curr['Currency Rate']).toFixed(4).toString() &&
            this.buyingExchRate != ''
          ) {
            let changeRate = await this.$showAlert(
              'warning',
              `Exchange Rate Mismatch`,
              `The exchange rate for ${curr['Currency Code']} is ${Number(curr['Currency Rate']).toFixed(4).toString()} but it has been changed to ${this.sellingExchRate}.<br>Do you want to change to the last exchange rate or remain the current exchange rate?`,
              [
                { text: 'Change to Latest Exchange Rate' },
                { text: 'Remain Current Exchange Rate', color: 'indianred', textColor: 'white' },
              ],
            );

            if (changeRate.includes('Latest')) {
              this.sellingExchRate = curr['Currency Rate']?.toString();
              this.buyingExchRate = curr['Currency Rate']?.toString();
              this.selectedBuyingCurrency = this.selectedSellingCurrency;
            }
          } else {
            if (
              this.allowDiffCurr == 'Y' &&
              this.recordSource == false &&
              this.selectedBuyingCurrency['Currency Code'] != this.selectedSellingCurrency['Currency Code']
            ) {
              let changeCurrency = await this.$showAlert(
                'warning',
                'Different Currency Detected',
                `Do you want to change buying currency to ${this.selectedSellingCurrency['Currency Code']}`,
                [
                  { text: `Change Buying Currency to ${this.selectedSellingCurrency['Currency Code']}` },
                  { text: 'Remain Current Buying Currency ', color: 'indianred', textColor: 'white' },
                ],
              );

              if (changeCurrency.startsWith('Change')) {
                this.buyingExchRate = curr['Currency Rate']?.toString();
                this.selectedBuyingCurrency = this.selectedSellingCurrency;
              }
            } else if (this.allowDiffCurr == 'N' && this.recordSource == false) {
              this.buyingExchRate = curr['Currency Rate']?.toString();
              this.$nextTick(() => {
                this.selectedBuyingCurrency = this.selectedSellingCurrency;
              });
            } else if (
              this.allowDiffCurr == 'N' &&
              this.selectedSellingCurrency['Currency Code'] != this.selectedSellingCurrencyOld['Currency Code']
            ) {
              this.$showAlert(
                'error',
                'Different Currency Detected',
                'You are not allowed to have different currency. Please contact administartor',
              );
              this.$nextTick(() => {
                this.selectedSellingCurrency = this.selectedSellingCurrencyOld;
              });
              return;
            }
            this.sellingExchRate = curr['Currency Rate']?.toString(); //move here due to if AllowDiffCurr is N then cannot change the original exch rate
          }
        } else {
          this.sellingExchRate = curr['Currency Rate']?.toString();
          if (this.allowDiffCurr == 'N' && this.recordSource == false) {
            this.buyingExchRate = curr['Currency Rate']?.toString();
            this.$nextTick(() => {
              this.selectedBuyingCurrency = this.selectedSellingCurrency;
            });
          }
        }

        this.selectedBuyingCurrencyOld = JSON.parse(JSON.stringify(this.selectedBuyingCurrency));
        this.buyingFactor = this.selectedBuyingCurrency['Factor'];
        this.selectedSellingCurrencyOld = JSON.parse(JSON.stringify(curr));
        this.sellingFactor = curr['Factor'];
      }
      this.calculateTax();
    },
    populateDetails: async function () {
      if (this.details) {
        this.qty = this.details['quantity'].toString() || '';
        this.selectedUOM =
          this.details['uom'] ?
            this.uomList.getSelected(this.details['UOM'], 'UOM Code') ?
              this.uomList.getSelected(this.details['uom'], 'UOM Code')
            : { 'UOM Code': this.details['uom'] }
          : null;
        if (this.details['buying']) {
          let buying = this.details['buying'];
          this.selectedBuyingCurrency =
            buying['currency'] ?
              this.currencyList.getSelected(buying['currency'], 'Currency Code')
            : this.currencyList.getSelected('MYR', 'Currency Code');
          if (this.selectedBuyingCurrency) {
            this.selectedBuyingCurrencyOld = JSON.parse(JSON.stringify(this.selectedBuyingCurrency));
          }

          this.buyingUnitPrice = Number(buying['unitPrice']).toFixed(4) || '';
          this.buyingTotalPrice = Number(buying['totalPrice']).toFixed(2) || '';
          this.buyingExchRate =
            buying['exchRate'] || this.currencyList.getSelected('MYR', 'Currency Code')['Currency Rate'];
          this.buyingFactor =
            this.selectedBuyingCurrency ?
              this.selectedBuyingCurrency['Factor']
            : this.currencyList.getSelected('MYR', 'Currency Code')['Factor'];
        }

        if (this.details['markup']) {
          let markup = this.details['markup'];

          this.markupUnitPrice = Number(markup['unitPrice']).toFixed(4) || '';
          this.markupTotalPrice = Number(markup['totalPrice']).toFixed(2) || '';
        }

        if (this.details['selling']) {
          let selling = this.details['selling'];

          this.selectedSellingCurrency =
            selling['currency'] ?
              this.currencyList.getSelected(selling['currency'], 'Currency Code')
            : this.currencyList.getSelected('MYR', 'Currency Code');
          if (this.selectedSellingCurrency) {
            this.selectedSellingCurrencyOld = JSON.parse(JSON.stringify(this.selectedSellingCurrency));
          }
          this.selectedBillingCustomer =
            selling['billingCustomer'] ?
              this.billCustList.getSelected(selling['billingCustomer'], 'Customer Code')
            : null;
          this.sellingUnitPrice = Number(selling['unitPrice']).toFixed(4) || '';
          this.sellingTotalPrice = Number(selling['totalPrice']).toFixed(2) || '';
          this.sellingExchRate =
            selling['exchRate'] || this.currencyList.getSelected('MYR', 'Currency Code')['Currency Rate'];
          this.sellingFactor =
            this.selectedSellingCurrency ?
              this.selectedSellingCurrency['Factor']
            : this.currencyList.getSelected('MYR', 'Currency Code')['Factor'];
        }
        if (this.qty) {
          if (this.buyingUnitPrice && !this.buyingTotalPrice) {
            // Auto calculate buying total price
            this.buyingTotalPrice = (Number(this.buyingUnitPrice) * Number(this.qty)).toFixed(2);
          }
          if (
            (!this.buyingUnitPrice || this.buyingUnitPrice == '' || this.buyingUnitPrice == '0.0000') &&
            this.buyingTotalPrice
          ) {
            // Auto calculate buying unit price
            this.buyingUnitPrice = (Number(this.buyingTotalPrice) / Number(this.qty)).toFixed(4);
          }
          if (this.sellingUnitPrice && !this.sellingTotalPrice) {
            this.sellingTotalPrice = (Number(this.sellingUnitPrice) * Number(this.qty)).toFixed(2);
          }
          if (
            (!this.sellingUnitPrice || this.sellingUnitPrice == '' || this.sellingUnitPrice == '0.0000') &&
            this.sellingTotalPrice
          ) {
            this.sellingUnitPrice = (Number(this.sellingTotalPrice) / Number(this.qty)).toFixed(4);
          }
        }

        if (this.enableTaxSection && this.selectedChargesCode) {
          this.srvTaxRate = this.details['tax']['srvTaxRate'];
          this.fsrvTaxRate = this.details['tax']['fsrvTaxRate'];

          this.netInclTax = Number(this.details['tax']['netInclTax']).toFixed(2);
          this.fnetInclTax = Number(this.details['tax']['fnetInclTax']).toFixed(2);
          this.srvTaxAmt = Number(this.details['tax']['srvTaxAmt']).toFixed(2);
          this.fsrvTaxAmt = Number(this.details['tax']['fsrvTaxAmt']).toFixed(2);
          this.selectedsstTaxCode = this.sstTaxCodeList.getSelected(this.details['tax']['taxCode'], 'Tax Code') ?? null;
          this.getTaxCode();
          // let temp = this.srvTaxRate ? (Number(this.srvTaxRate)/100 * Number(this.netInclTax)) : '0.00';
          // this.srvTaxAmt  = temp?.toString();
          // let tempfsrvTaxAmt = this.fsrvTaxRate ? (Number(this.fsrvTaxRate)/100 * Number(this.fnetInclTax)) : '0.00';
          // this.fsrvTaxAmt = tempfsrvTaxAmt?.toString();
        }
      }
    },
    clearErrMsg: function () {
      this.qtyError = '';
      this.buyingCurrencyError = '';
      this.buyingUnitPriceError = '';
      this.buyingTotalPriceError = '';
      this.buyingExchRateError = '';

      this.markupUnitPriceError = '';
      this.markupTotalPriceError = '';

      this.sellingCurrencyError = '';
      this.sellingUnitPriceError = '';
      this.sellingTotalPriceError = '';
      this.sellingExchRateError = '';
      this.netInclTaxError = '';
    },
    clearAll: function () {
      this.buyingUnitPrice = '';
      this.buyingTotalPrice = '';
      this.markupUnitPrice = '';
      this.markupTotalPrice = '';
      this.sellingUnitPrice = '';
      this.sellingTotalPrice = '';
      this.fnetInclTax = '';
      this.netInclTax = '';

      this.selectedBuyingCurrency = null;
      this.selectedBuyingCurrencyOld = null;
      this.buyingUnitPrice = '';
      this.buyingTotalPrice = '';
      this.buyingExchRate = '';
      this.buyingFactor = '';

      this.selectedSellingCurrency = null;
      this.selectedSellingCurrencyOld = null;
      this.sellingUnitPrice = '';
      this.sellingUnitPriceDisabled = '';
      this.sellingTotalPrice = '';
      this.sellingTotalPriceDisabled = '';
      this.sellingExchRate = '';
      this.sellingExchRateDisabled = '';
      this.sellingFactor = '';

      this.selectedsstTaxCode = null;
      this.selectedTaxCode = null;
      this.sstTaxCode = null;
      this.srvTaxRate = '';
      // this.myrEquivalentMarkupUnitPrice = '';
      // this.myrEquivalentMarkupTotalPrice = '';
    },
    getTaxCode: function () {
      if (this.enableTaxSection && this.taxCodeRate && this.selectedChargesCode) {
        this.srvTaxRate = this.taxCodeRate['srvTaxRate'] ?? '0';
        if (this.selectedsstTaxCode == null) {
          this.selectedsstTaxCode =
            this.taxCodeRate['sstTaxCode'] == '' ?
              null
            : this.sstTaxCodeList.getSelected(this.taxCodeRate['sstTaxCode'], 'Tax Code');
        }
        this.disabledTaxCodeField = false;
        if (this.srvTaxRate == '0' && this.selectedChargesCode['SST Rate %'] == '0' && this.internalCustomer == '0') {
          this.selectedsstTaxCode = null;
          this.sstTaxCode = null;
          this.srvTaxRate = this.selectedChargesCode['SST Rate %'].toString();
          this.disabledTaxCodeField = true;
        }

        if (!this.taxCodeRate?.['SASEnableTaxCode']) {
          this.selectedsstTaxCode = null;
          this.sstTaxCode = null;
          this.disabledTaxCodeField = true;
        }
        if (!this.taxCodeRate?.['SASEnableTaxRate']) {
          this.srvTaxRate = 0;
        }
        this.calculateTax();
        this.$nextTick(() => {
          this.selectedUOM =
            this.selectedChargesCode['UOM'] != '' ?
              (this.uomList.getSelected(this.selectedChargesCode['UOM'], 'UOM Code') ?? null)
            : (this.selectedUOM ?? null);
        });
      }
    },
    compareValues: function (val1, val2) {
      // Convert both values to strings
      const strA = String(val1);
      const strB = String(val2);

      if (strA === strB) {
        return true;
      }
      // Remove trailing zeros if any
      const cleanA = strA.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
      const cleanB = strB.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
      // Compare the cleaned values
      return cleanA === cleanB;
    },
    //#region Public Functions
    getDetails: function () {
      //#region Checking Compulsory
      let error = false;
      if (!this.qty) {
        error = true;
        this.qtyError = 'Quantity is Required!';
      }
      if (Number(this.qty) < 0 || this.qty == '0') {
        error = true;
        this.qtyError = 'Quantity is Invalid!';
      }
      if (!this.selectedBuyingCurrency) {
        error = true;
        this.buyingCurrencyError = 'Currency is Required!';
      }
      if (!this.buyingUnitPrice && !this.buyingIsHidden) {
        error = true;
        this.buyingUnitPriceError = 'Unit Price is Required!';
      }
      if (!this.buyingTotalPrice && !this.buyingIsHidden) {
        error = true;
        this.buyingTotalPriceError = 'Total Price is Required!';
      }
      if (!this.buyingExchRate) {
        error = true;
        this.buyingExchRateError = 'Exchange Rate is Required!';
      }
      if (Number(this.buyingExchRate) == 0 && !this.buyingIsHidden) {
        error = true;
        this.buyingExchRateError = 'Exchange Rate is Invalid!';
      }
      if (!this.markupUnitPrice) {
        error = true;
        this.markupUnitPriceError = 'Unit Price is Required!';
      }
      if (!this.markupTotalPrice) {
        error = true;
        this.markupTotalPriceError = 'Total Price is Required!';
      }
      if (!this.selectedSellingCurrency) {
        error = true;
        this.sellingCurrencyError = 'Currency is Required!';
      }
      if (this.headerCurrency != null) {
        if (
          this.selectedSellingCurrency &&
          this.headerCurrency?.['Currency Code'] != 'MYR' &&
          this.selectedSellingCurrency['Currency Code'] != this.headerCurrency['Currency Code']
        ) {
          error = true;
          this.sellingCurrencyError = 'Selected currency is different from header currency!';
        }
        if (
          this.selectedSellingCurrency &&
          this.headerCurrency?.['Currency Code'] != 'MYR' &&
          !this.compareValues(this.sellingExchRate, this.headerRate)
        ) {
          error = true;
          this.sellingExchRateError = 'Selected currency rate is different from header currency rate!';
        }
      }
      if (!this.sellingUnitPrice && !this.sellingIsHidden) {
        error = true;
        this.sellingUnitPriceError = 'Unit Price is Required!';
      }
      if (!this.sellingTotalPrice && !this.sellingIsHidden) {
        error = true;
        this.sellingTotalPriceError = 'Total Price is Required!';
      }
      if (!this.sellingExchRate) {
        error = true;
        this.sellingExchRateError = 'Exchange Rate is Required!';
      }
      if (Number(this.sellingExchRate) == 0 && !this.sellingIsHidden) {
        error = true;
        this.sellingExchRateError = 'Exchange Rate is Invalid!';
      }
      if (this.enableTaxSection && !this.netInclTax) {
        error = true;
        this.netInclTaxError = 'Net Incl. Tax is Required!';
      }
      //#endregion

      if (error) {
        return null;
      } else {
        let det = {
          quantity: this.qty,
          uom: this.selectedUOM,
          buying: {
            currency: this.selectedBuyingCurrency,
            unitPrice: this.buyingUnitPrice,
            totalPrice: this.buyingTotalPrice,
            exchRate: this.buyingExchRate,
          },
          markup: {
            unitPrice: this.markupUnitPrice,
            totalPrice: this.markupTotalPrice,
          },
          selling: {
            currency: this.selectedSellingCurrency,
            unitPrice: this.sellingUnitPrice,
            totalPrice: this.sellingTotalPrice,
            exchRate: this.sellingExchRate,
          },
          myrEquivalent: {
            buyingUnitPrice: this.myrEquivalentBuyingUnitPrice,
            buyingTotalPrice: this.myrEquivalentBuyingTotalPrice,
            markupUnitPrice:
              !this.markupUnitPrice || this.markupUnitPrice == '0.0000' ? '' : this.myrEquivalentMarkupUnitPrice,
            markupTotalPrice:
              !this.markupTotalPrice || this.markupTotalPrice == '0.00' ? '' : this.myrEquivalentMarkupTotalPrice,
            sellingUnitPrice: this.myrEquivalentSellingUnitPrice,
            sellingTotalPrice: this.myrEquivalentSellingTotalPrice,
          },
          tax: {
            taxCode: this.selectedsstTaxCode ?? null,
            sstTaxCode: this.sstTaxCode ? this.sstTaxCode : (this.selectedsstTaxCode?.['Tax Code'] ?? ''),
            taxRate: this.taxRate,
            taxAmt: this.taxAmt,

            srvTaxRate: this.srvTaxRate,
            fsrvTaxRate: this.fsrvTaxRate,

            netInclTax: this.netInclTax,
            fnetInclTax: this.fnetInclTax,
            srvTaxAmt: this.srvTaxAmt,
            fsrvTaxAmt: this.fsrvTaxAmt,
          },
        };
        this.clearErrMsg();
        return det;
      }
    },
    //#endregion
  },
  async mounted() {
    this.isLoading = true;
    this.uomList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'UOM' });
    this.currencyList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'currency' });
    this.tableDataType = {
      '#': 'number',
      'Currency Code': 'string',
      Description: 'string',
      'Currency Rate': 'number',
      'Exchange Date': 'date',
      Factor: 'number',
    };
    this.allowDiffCurr =
      (await this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=FINPay_AllowDiffCurr'))?.[0]?.['SettingsValue'] ??
      'N';
    this.sstTaxCodeList = this.$deepTrimJsonV3(
      await this.$apiCallV2(`/Master/GetListMaster`, { masterfile: 'ARTAXCODE', module: 'SST' }),
    );
    // this.gstCodeList = this.$deepTrimJsonV3(await this.$apiCallV2(`/Master/GetListMaster`, { masterfile: 'GST' }));

    // let companyList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'COMPANY' });
    // let details = (await this.$apiCallV2('/Master/GetDetailsMaster', { "masterfile": "COMPANY", "module": "MASTER", "recordid": companyList[0]['Company Code'], }))["Company"]
    // this.GSTEnabled = details['CompanyGST2Enabled'] == 1 ? true : false;
    this.GSTEnabled = false;

    this.isLoading = false;

    this.populateDetails();
  },
  watch: {
    sellingExchRate: function (val) {
      if (val) {
        this.sellingExchRate = Number(val).toFixed(4);
      }
    },
    buyingExchRate: function (val) {
      if (val) {
        this.buyingExchRate = Number(val).toFixed(4);
      }
    },
    headerCurrency: function (val) {
      if (val && this.action == 'N') {
        this.currencySelected(val, true);
      }
    },
    headerRate: function (val) {
      if (val && this.qty) {
        this.sellingExchRate = Number(val).toFixed(4);
        this.buyingExchRate = Number(val).toFixed(4);
      }
    },
    taxCodeRate: function () {
      this.getTaxCode();
    },
    selectedChargesCode: function () {
      this.getTaxCode();
    },
    fieldDisabled: async function (val) {
      if (!val) {
        if (!this.selectedBuyingCurrency) {
          this.selectedBuyingCurrency = this.currencyList.getSelected('MYR', 'Currency Code');
          this.currencySelected(this.selectedBuyingCurrency, true);
        }
        if (!this.selectedSellingCurrency) {
          this.selectedSellingCurrency = this.currencyList.getSelected('MYR', 'Currency Code');
          this.currencySelected(this.selectedSellingCurrency, false);
        }
      }

      this.clearErrMsg();
    },
    payableChecking: function (val) {
      if (val) {
        //clear the value if there is changes
        // this.buyingUnitPrice = '';
        // this.buyingTotalPrice = '';
        // this.markupUnitPrice = '';
        // this.markupTotalPrice = '';
        // this.sellingUnitPrice = '';
        // this.sellingTotalPrice = '';
        // this.fnetInclTax = '';
        // this.netInclTax = '';
        this.clearErrMsg();
      }
    },
    qty: function (val) {
      if (Number(val) < 0) {
        error = true;
        this.qtyError = 'Quantity is Invalid!';
        return;
      }
      let quantity = Number(val);
      if (this.buyingUnitPrice) this.buyingTotalPrice = (Number(this.buyingUnitPrice) * quantity).toFixed(2).toString();
      if (this.markupUnitPrice) this.markupTotalPrice = (Number(this.markupUnitPrice) * quantity).toFixed(2).toString();
      if (this.sellingUnitPrice)
        this.sellingTotalPrice = (Number(this.sellingUnitPrice) * quantity).toFixed(2).toString();
      if (this.headerCurrency?.['Currency Code'] != 'MYR' && this.headerRate != '') {
        //foreign currency should always follow header currency
        this.sellingExchRate = Number(this.headerRate).toFixed(4);
        this.buyingExchRate = Number(this.headerRate).toFixed(4);
      }
      if (this.enableTaxSection) {
        if (!this.taxCodeRate?.['SASEnableTaxCode']) {
          this.selectedsstTaxCode = null;
          this.sstTaxCode = null;
          this.disabledTaxCodeField = true;
        }
        if (!this.taxCodeRate?.['SASEnableTaxRate']) {
          this.srvTaxRate = 0;
        }
      }
      this.calculateTax();
    },
    selectedBuyingCurrency: {
      handler: function (val) {
        if (!val) {
          // if selectedBuyingCurrency is cleared or nothing is selected yet
          return; // don't do anything
        }
        if (!this.selectedSellingCurrency) {
          // if nothing is selected for selectedSellingCurrency
          this.markupUnitPrice = '0.0000'; // set these 2 data fields to 0
          this.markupTotalPrice = '0.00';
        }
        if (val['Currency Code'] != this.selectedSellingCurrency?.['Currency Code']) {
          // if buying currency and selling currency is different
          this.markupUnitPrice = '0.0000'; // set these 2 data fields to 0
          this.markupTotalPrice = '0.00';
        }
        if (this.allowDiffCurr == 'N' && this.action == 'N' && this.payableChecking == '1') {
          this.selectedSellingCurrency = val;
          this.sellingExchRate = this.buyingExchRate;
        }
      },
      deep: true,
    },
    selectedSellingCurrency: {
      handler: function (val) {
        if (!val) {
          // if selectedSellingCurrency is cleared or nothing is selected yet
          return; // don't do anything
        }
        if (!this.selectedBuyingCurrency) {
          // if nothing is selected for selectedBuyingCurrency
          this.markupUnitPrice = '0.0000'; // set these 2 data fields to 0
          this.markupTotalPrice = '0.00';
          // this.markupUnitPrice = this.sellingUnitPrice;
          // this.markupTotalPrice = this.sellingTotalPrice;
        }
        if (val['Currency Code'] != this.selectedBuyingCurrency?.['Currency Code']) {
          // if buying currency and selling currency is different
          this.markupUnitPrice = '0.0000'; // set these 2 data fields to 0
          this.markupTotalPrice = '0.00';
        }
        if (this.allowDiffCurr == 'N' && this.action == 'N' && this.payableChecking == '1') {
          this.selectedBuyingCurrency = val;
          this.buyingExchRate = this.sellingExchRate;
        }
      },
      deep: true,
    },
    details: function () {
      this.selectedBuyingCurrencyOld = null;
      this.selectedSellingCurrencyOld = null;
      this.populateDetails();
    },
    selectedsstTaxCode(newVal, oldVal) {
      if (newVal === oldVal) return;
      if (this.srvTaxRate == '0' && this.internalCustomer != '0') {
        this.$showAlert('error', 'Service Tax does not apply to charge code', 'Exemption not applicable');
        this.selectedsstTaxCode = null;
        this.sstTaxCode = null;
        return;
      }
      // Use new value or fallback to charge code rate
      this.srvTaxRate =
        newVal ? newVal['RATE'].toString() : (this.selectedChargesCode?.['SST Rate %']?.toString() ?? '');
      this.sstTaxCode = newVal?.['Tax Code'] ?? '';
      //console.log('Updated srvTaxRate:', this.srvTaxRate);
      this.calculateTax();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.foreign-currency-main {
  font-size: 12px;
  padding: 10px;
  border-radius: 10px;
  position: relative;
  background-color: rgb(225, 255, 255);
}
.foreign-currency-controls {
  position: relative;
  z-index: 101;
}
.foreign-currency-row {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 30px;
  flex-direction: row;

  > :not(:first-child) {
    margin-left: 5px;
  }

  > .foreign-currency-column {
    flex: 1;
  }
}
.foreign-currency-loading {
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background-color: rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 102;

  > .foreign-currency-loading-spinner {
    border-style: solid;
    border-width: 5px;
    border-color: transparent white white white;
    height: 35px;
    width: 35px;
    border-radius: 100%;
    animation: spin 1s ease-in-out infinite;
  }
}
.foreign-currency-background {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: row;
  z-index: 1;

  > .background-left {
    // flex: 1;
    width: calc(33.33% + 4px);
    // background-color: rgb(225, 255, 255);
    border-right: 3px solid white;
  }

  > .background-middle {
    flex: 1;
    // background-color: transparent;
    border-left: 3px solid white;
    border-right: 3px solid white;
  }

  > .background-right {
    // flex: 1;
    width: calc(33.33% + 4px);
    // background-color: rgb(225, 255, 255);
    border-left: 3px solid white;
  }
}
</style>

<style>
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
